import type { HsDecodedLine, HsDecodedMesh, HsDecodedPointCloud } from '@sibe/hyperscene';

/** Geometry ids from the API differ in case; the store keys them in lower case. */
export function hsNormalizeGeometryId(value: string): string {
  return value.trim().toLowerCase();
}

export function hsCountMeshTriangles(mesh: HsDecodedMesh): number {
  const indicesLength: number = mesh.indices?.length ?? 0;
  if (indicesLength > 0) {
    return Math.floor(indicesLength / 3);
  }
  return Math.floor((mesh.vertices?.length ?? 0) / 9);
}

export function hsCountLineSegments(line: HsDecodedLine): number {
  return Math.floor((line.vertices?.length ?? 0) / 6);
}

export function hsCountPointCloudPoints(pointCloud: HsDecodedPointCloud): number {
  return Math.floor((pointCloud.points?.length ?? 0) / 3);
}

/**
 * Approximate CPU bytes of a decoded mesh. `counted` skips arrays already counted for another entry,
 * such as those a corrected original shares with its source.
 */
export function hsEstimateMeshBytes(mesh: HsDecodedMesh, counted?: Set<ArrayLike<number>>): number {
  const bytes = (values: ArrayLike<number> | undefined): number => {
    if (values === undefined || counted?.has(values) === true) {
      return 0;
    }
    counted?.add(values);
    return ArrayBuffer.isView(values) ? values.byteLength : values.length * 8;
  };
  return (
    bytes(mesh.vertices) + bytes(mesh.normals) + bytes(mesh.uvs) + bytes(mesh.indices) + bytes(mesh.canonicalIndices)
  );
}

export interface HsPointCloudLimits {
  /** Points kept across all decoded point clouds. */
  points: number;
  /** Decoded point cloud geometries kept. */
  geometries: number;
}

/**
 * Decoded geometry of the open scene with running totals. Meshes have a byte budget: geometry the view
 * no longer shows is dropped first. Point clouds are held to point and geometry limits.
 */
export class HsDecodedGeometryStore {
  public readonly meshes: Map<string, HsDecodedMesh> = new Map();
  public readonly lines: Map<string, HsDecodedLine> = new Map();
  public readonly pointClouds: Map<string, HsDecodedPointCloud> = new Map();
  /** Originals of deduplicated meshes, decoded to check material splits against. */
  public readonly originals: Map<string, HsDecodedMesh> = new Map();
  /** Corrected originals keyed by `nodeId|geometryId`. */
  public readonly corrected: Map<string, HsDecodedMesh> = new Map();
  /** `nodeId|geometryId` keys whose original source has been checked. */
  public readonly checkedOriginalSources: Set<string> = new Set();
  public loadedTriangles: number = 0;
  public loadedLineSegments: number = 0;
  public loadedPoints: number = 0;
  private contextKey: string | null = null;

  /** Switches to another scene and model; returns true when that cleared the store. */
  public useContext(sceneId: string, modelId: string): boolean {
    const key: string = `${sceneId}|${modelId}`;
    if (this.contextKey === key) {
      return false;
    }
    this.contextKey = key;
    this.clear();
    return true;
  }

  public setMesh(mesh: HsDecodedMesh): void {
    const id: string = hsNormalizeGeometryId(mesh.geometryId);
    const previous: HsDecodedMesh | undefined = this.meshes.get(id);
    if (previous !== undefined) {
      this.loadedTriangles -= hsCountMeshTriangles(previous);
    }
    this.meshes.set(id, mesh);
    this.loadedTriangles += hsCountMeshTriangles(mesh);
  }

  public setLine(line: HsDecodedLine): void {
    const id: string = hsNormalizeGeometryId(line.geometryId);
    const previous: HsDecodedLine | undefined = this.lines.get(id);
    if (previous !== undefined) {
      this.loadedLineSegments -= hsCountLineSegments(previous);
    }
    this.lines.set(id, line);
    this.loadedLineSegments += hsCountLineSegments(line);
  }

  public setPointCloud(pointCloud: HsDecodedPointCloud): void {
    const id: string = hsNormalizeGeometryId(pointCloud.geometryId);
    const previous: HsDecodedPointCloud | undefined = this.pointClouds.get(id);
    if (previous !== undefined) {
      this.loadedPoints -= hsCountPointCloudPoints(previous);
    }
    this.pointClouds.set(id, pointCloud);
    this.loadedPoints += hsCountPointCloudPoints(pointCloud);
  }

  public clear(): void {
    this.clearOriginals();
    this.meshes.clear();
    this.lines.clear();
    this.pointClouds.clear();
    this.loadedTriangles = 0;
    this.loadedLineSegments = 0;
    this.loadedPoints = 0;
  }

  public clearOriginals(): void {
    this.checkedOriginalSources.clear();
    this.originals.clear();
    this.corrected.clear();
  }

  /** Bytes of decoded scene meshes, originals and corrected originals; shared arrays count once. */
  public meshBytes(): number {
    const counted: Set<ArrayLike<number>> = new Set<ArrayLike<number>>();
    let total: number = 0;
    for (const map of [this.meshes, this.originals, this.corrected]) {
      for (const mesh of map.values()) {
        total += hsEstimateMeshBytes(mesh, counted);
      }
    }
    return total;
  }

  public meshCount(): number {
    return this.meshes.size + this.originals.size + this.corrected.size;
  }

  /**
   * Drops meshes the view does not use until the budget holds: scene meshes outside `activeMeshIds`,
   * then corrected originals outside `activeSourceKeys`, then any decoded original. Returns false when
   * the store was already within the budget.
   */
  public trimInactiveMeshes(
    budgetBytes: number,
    activeMeshIds: ReadonlySet<string>,
    activeSourceKeys: ReadonlySet<string>
  ): boolean {
    let total: number = this.meshBytes();
    if (total <= budgetBytes) {
      return false;
    }
    for (const [id, mesh] of this.meshes) {
      if (total <= budgetBytes) {
        break;
      }
      if (!activeMeshIds.has(id)) {
        this.meshes.delete(id);
        total -= hsEstimateMeshBytes(mesh);
        this.loadedTriangles -= hsCountMeshTriangles(mesh);
      }
    }
    for (const [sourceKey, mesh] of this.corrected) {
      if (total <= budgetBytes) {
        break;
      }
      if (!activeSourceKeys.has(sourceKey)) {
        this.corrected.delete(sourceKey);
        this.checkedOriginalSources.delete(sourceKey);
        total -= hsEstimateMeshBytes(mesh);
      }
    }
    for (const [id, mesh] of this.originals) {
      if (total <= budgetBytes) {
        break;
      }
      this.originals.delete(id);
      total -= hsEstimateMeshBytes(mesh);
    }
    return true;
  }

  /**
   * Drops point clouds outside `activeIds` until both limits hold and recounts the loaded points.
   * Returns false when nothing had to be dropped.
   */
  public prunePointClouds(limits: HsPointCloudLimits, activeIds: ReadonlySet<string>): boolean {
    let points: number = 0;
    for (const pointCloud of this.pointClouds.values()) {
      points += hsCountPointCloudPoints(pointCloud);
    }
    let geometries: number = this.pointClouds.size;
    if (points <= limits.points && geometries <= limits.geometries) {
      this.loadedPoints = points;
      return false;
    }
    for (const [id, pointCloud] of this.pointClouds) {
      if (activeIds.has(id) || (points <= limits.points && geometries <= limits.geometries)) {
        continue;
      }
      this.pointClouds.delete(id);
      points -= hsCountPointCloudPoints(pointCloud);
      geometries--;
    }
    this.loadedPoints = Math.max(0, points);
    return true;
  }
}
